/**
 * /pilot/admin — pilot invite + leads admin console.
 *
 * The static chrome (gate, tabs, panes, modal) is React JSX styled with
 * Oatmeal taupe tokens + Inter / Mona Sans. All IDs, data attributes,
 * and the `.admin-tab` / `.admin-pane` class hooks are preserved
 * verbatim so `lib/pilot/admin.js`'s imperative DOM wiring still
 * resolves (token gate, tab switching, invite/lead row rendering,
 * edit modal).
 *
 * Note on Oatmeal kit components: this page intentionally uses its own
 * design system (`admin.css`) layered on the Oatmeal taupe theme
 * tokens, NOT the marketing-kit `<Container>` / `<Heading>` etc.
 * primitives. The admin console is a data-table interface; the
 * marketing-kit vocabulary doesn't fit. F11 of the Oatmeal rebuild
 * scoped the work, found this divergence is intentional, and left
 * admin's chrome alone.
 *
 * admin.js talks to the backend via POST /api/graphql with an
 * X-Admin-Token header — same-origin under Firebase Hosting's /api/**
 * rewrite.
 *
 * Rendering happens once on mount; admin.js then takes over
 * imperative DOM ops, so we never re-render and React doesn't fight
 * with its mutations.
 */

import React, {useEffect, useRef} from 'react';
import {useDocumentTitle} from '../hooks/useDocumentTitle';
import {useOsDarkMode} from '../hooks/useOsDarkMode';
import '../styles/oatmeal.css';
import '../styles/admin.css';

export default function PilotAdminPage() {
  const mountedRef = useRef(false);

  useDocumentTitle('Planapus — Admin');
  useOsDarkMode();

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    // Imported after the JSX is committed to the DOM so admin.js's
    // querySelector wiring resolves against the rendered nodes.
    import('../lib/pilot/admin').catch((err) => {
      console.error('[pilot-admin] Failed to load admin.js:', err);
    });
  }, []);

  // Intentionally NOT carrying `.oatmeal-root` — that wrapper's
  // `.dark .oatmeal-root` override swaps to a dark bg in dark mode,
  // but admin.css's text tokens (--a-text = taupe-950, etc.) are
  // designed only for the cream `--a-bg` and read as dark-on-dark
  // when the bg flips. `.pp-admin-root` already provides the
  // font-smoothing / bg / color / font-family that `.oatmeal-root`
  // would.
  return (
    <div className="pp-admin-root min-h-screen">
      <header className="admin-topbar">
        <div className="admin-logo">
          <span className="font-display text-lg tracking-tight">Planapus</span>{' '}
          <span className="text-taupe-500">· Admin</span>
        </div>
        <button
          className="admin-link"
          id="admin-logout"
          type="button"
          hidden
        >
          Log out
        </button>
      </header>

      {/* ============ LOGIN GATE ============ */}
      <div className="admin-gate" id="admin-gate">
        <div className="admin-card">
          <h1>Admin access</h1>
          <p className="admin-muted">
            Paste the shared admin token. Stored only in this browser.
          </p>
          <form id="admin-gate-form" className="admin-form">
            <input
              className="admin-input"
              id="admin-token-input"
              type="password"
              placeholder="PLANAPUS_ADMIN_TOKEN"
              autoComplete="off"
              required
            />
            <button className="admin-btn-primary" type="submit">
              Sign in
            </button>
            <p className="admin-error" id="admin-gate-error" />
          </form>
        </div>
      </div>

      {/* ============ APP ============ */}
      <main className="admin-main" id="admin-main" hidden>
        {/* Tabs */}
        <nav className="admin-tabs" role="tablist">
          <button className="admin-tab active" data-tab="invites" type="button">
            Invites
          </button>
          <button className="admin-tab" data-tab="leads" type="button">
            Leads
          </button>
          <button className="admin-tab" data-tab="new" type="button">
            + New invite
          </button>
          <button
            className="admin-tab admin-tab-action-items"
            data-tab="action-items"
            type="button"
          >
            <svg
              className="admin-tab-icon"
              viewBox="0 0 20 20"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="2.5" y="3.5" width="4" height="13" rx="1" />
              <rect x="8" y="3.5" width="4" height="8" rx="1" />
              <rect x="13.5" y="3.5" width="4" height="5" rx="1" />
            </svg>
            <span>Action items</span>
            <span className="admin-tab-badge" id="admin-tab-action-items-count" />
          </button>
          <button className="admin-tab" data-tab="design" type="button">
            Design options
          </button>
        </nav>

        {/* Invites tab */}
        <section className="admin-pane" data-pane="invites">
          <header className="admin-pane-head">
            <h2>Active &amp; revoked invites</h2>
            <button
              className="admin-link"
              id="admin-invites-refresh"
              type="button"
            >
              ↻ Refresh
            </button>
          </header>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Audience</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Code</th>
                  <th>Used</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody id="admin-invites-body">
                <tr>
                  <td colSpan={9} className="admin-muted">
                    Loading…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Leads tab */}
        <section className="admin-pane" data-pane="leads" hidden>
          <header className="admin-pane-head">
            <h2>Captured leads</h2>
            <button
              className="admin-link"
              id="admin-leads-refresh"
              type="button"
            >
              ↻ Refresh
            </button>
          </header>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Source</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Goals</th>
                </tr>
              </thead>
              <tbody id="admin-leads-body">
                <tr>
                  <td colSpan={7} className="admin-muted">
                    Loading…
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* New invite tab */}
        <section className="admin-pane" data-pane="new" hidden>
          <h2>Create an invite</h2>
          <p className="admin-muted">
            The link is the access token. If you enter an email, we'll email
            the link to them automatically.
          </p>
          <form id="admin-new-form" className="admin-form admin-form-grid">
            <label className="admin-label">
              Email <span className="admin-muted">optional</span>
              <input
                className="admin-input"
                name="email"
                type="email"
                placeholder="invitee@company.com"
                autoComplete="off"
              />
            </label>
            <label className="admin-label">
              Name <span className="admin-muted">optional</span>
              <input
                className="admin-input"
                name="name"
                type="text"
                placeholder="Jane Doe"
                autoComplete="off"
              />
            </label>
            <label className="admin-label">
              Company <span className="admin-muted">optional</span>
              <input
                className="admin-input"
                name="company"
                type="text"
                placeholder="Acme Corp"
                autoComplete="off"
              />
            </label>
            <label className="admin-label">
              Audience
              <select className="admin-input" name="audience" defaultValue="CUSTOMER">
                <option value="CUSTOMER">Customer — Story + Sandbox</option>
                <option value="VC">VC — Pitch deck only</option>
              </select>
            </label>
            <label className="admin-label admin-form-grid-full">
              Internal note{' '}
              <span className="admin-muted">
                optional, never shown to invitee
              </span>
              <input
                className="admin-input"
                name="note"
                type="text"
                placeholder="Met at AI2 demo day; food-mfg focus"
                autoComplete="off"
              />
            </label>
            <div className="admin-form-grid-full">
              <button className="admin-btn-primary" type="submit">
                Create invite
              </button>
              <p className="admin-error" id="admin-new-error" />
            </div>
          </form>

          <div className="admin-result" id="admin-new-result" hidden>
            <p className="admin-result-label">Invite link</p>
            <div className="admin-result-row">
              <input
                className="admin-input admin-result-input"
                id="admin-new-url"
                readOnly
              />
              <button
                className="admin-btn-secondary"
                id="admin-new-copy"
                type="button"
              >
                Copy
              </button>
            </div>
            <p className="admin-muted" id="admin-new-meta" />
          </div>
        </section>

        {/* Action items tab — kanban backed by Airtable. admin.ts renders
            cards into #admin-action-items-board on tab activation. */}
        <section className="admin-pane" data-pane="action-items" hidden>
          <header className="admin-pane-head">
            <h2>Action items</h2>
            <div className="admin-pane-head-actions">
              <span className="admin-muted" id="admin-action-items-status" />
              <button
                className="admin-link"
                id="admin-action-items-refresh"
                type="button"
              >
                ↻ Refresh
              </button>
            </div>
          </header>
          <p className="admin-muted">
            Drag a card between columns to change its status. Click the
            pencil to edit title, owner, priority, due date, or notes.
            Writes go straight to Airtable.
          </p>
          <div className="admin-kanban" id="admin-action-items-board">
            <p className="admin-muted">Loading…</p>
          </div>
        </section>

        {/* Design options tab */}
        <section className="admin-pane" data-pane="design" hidden>
          <header className="admin-pane-head">
            <h2>Pilot design options</h2>
            <a
              className="admin-link"
              href="/pilot-design-options.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab ↗
            </a>
          </header>
          <p className="admin-muted">
            Same /pilot content rendered in 18 design languages — today's
            brand-v2 plus 13 Tailwind Plus templates and 4 showcase picks
            (Vercel, Linear, Supabase, Resend).
          </p>
          <iframe
            id="admin-design-frame"
            src="/pilot-design-options.html"
            title="Pilot design options"
            className="admin-design-frame"
            loading="lazy"
          />
        </section>
      </main>

      {/* Edit action-item modal */}
      <div className="admin-modal-backdrop" id="admin-action-edit-modal" hidden>
        <div
          className="admin-modal"
          role="dialog"
          aria-labelledby="admin-action-edit-title"
        >
          <header className="admin-modal-head">
            <h3 id="admin-action-edit-title">Edit action item</h3>
            <button
              className="admin-link"
              id="admin-action-edit-close"
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <form id="admin-action-edit-form" className="admin-form admin-form-grid">
            <p
              className="admin-muted admin-form-grid-full"
              id="admin-action-edit-meta"
            />
            <label className="admin-label admin-form-grid-full">
              Title
              <input
                className="admin-input"
                name="title"
                type="text"
                autoComplete="off"
              />
            </label>
            <label className="admin-label">
              Owner
              <input
                className="admin-input"
                name="owner"
                type="text"
                autoComplete="off"
              />
            </label>
            <label className="admin-label">
              Priority
              <select className="admin-input" name="priority">
                <option value="">—</option>
                <option value="P0">P0</option>
                <option value="P1">P1</option>
                <option value="P2">P2</option>
              </select>
            </label>
            <label className="admin-label">
              Status
              <select className="admin-input" name="status">
                <option value="pending">pending</option>
                <option value="open">open</option>
                <option value="in-progress">in-progress</option>
                <option value="resolved">resolved</option>
                <option value="stale">stale</option>
                <option value="cancelled">cancelled</option>
              </select>
            </label>
            <label className="admin-label">
              Due date
              <input
                className="admin-input"
                name="dueDate"
                type="date"
                autoComplete="off"
              />
            </label>
            <label className="admin-label admin-form-grid-full">
              Notes
              <textarea
                className="admin-input"
                name="notes"
                rows={4}
                autoComplete="off"
              />
            </label>
            <label className="admin-label admin-form-grid-full">
              Outcome{" "}
              <span className="admin-muted">recorded when resolved</span>
              <textarea
                className="admin-input"
                name="outcome"
                rows={3}
                autoComplete="off"
              />
            </label>
            <div className="admin-form-grid-full admin-modal-actions">
              <button
                className="admin-btn-secondary"
                type="button"
                id="admin-action-edit-cancel"
              >
                Cancel
              </button>
              <button className="admin-btn-primary" type="submit">
                Save changes
              </button>
            </div>
            <p
              className="admin-error admin-form-grid-full"
              id="admin-action-edit-error"
            />
          </form>
        </div>
      </div>

      {/* Resolve outcome prompt — opens when a card is dropped into the
          Resolved column. Save writes outcome + status; Skip writes status
          only; Cancel reverts the optimistic move. */}
      <div className="admin-modal-backdrop" id="admin-resolve-modal" hidden>
        <div
          className="admin-modal admin-modal-compact"
          role="dialog"
          aria-labelledby="admin-resolve-title"
        >
          <header className="admin-modal-head">
            <h3 id="admin-resolve-title">
              Resolve:{" "}
              <span
                className="admin-resolve-title-text"
                id="admin-resolve-title-text"
              />
            </h3>
            <button
              className="admin-link"
              id="admin-resolve-close"
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <form id="admin-resolve-form" className="admin-form">
            <label className="admin-label">
              How did this resolve?{" "}
              <span className="admin-muted">optional</span>
              <textarea
                className="admin-input"
                id="admin-resolve-outcome"
                name="outcome"
                rows={4}
                placeholder="e.g., Shipped via PR #123 · Customer confirmed in Slack"
                autoComplete="off"
              />
            </label>
            <div className="admin-modal-actions">
              <button
                className="admin-btn-secondary"
                type="button"
                id="admin-resolve-cancel"
              >
                Cancel move
              </button>
              <button
                className="admin-btn-secondary"
                type="button"
                id="admin-resolve-skip"
              >
                Skip outcome
              </button>
              <button className="admin-btn-primary" type="submit">
                Save outcome
              </button>
            </div>
            <p className="admin-error" id="admin-resolve-error" />
          </form>
        </div>
      </div>

      {/* Edit-invite modal */}
      <div className="admin-modal-backdrop" id="admin-edit-modal" hidden>
        <div
          className="admin-modal"
          role="dialog"
          aria-labelledby="admin-edit-title"
        >
          <header className="admin-modal-head">
            <h3 id="admin-edit-title">Edit invite</h3>
            <button
              className="admin-link"
              id="admin-edit-close"
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <form id="admin-edit-form" className="admin-form admin-form-grid">
            <p className="admin-muted admin-form-grid-full" id="admin-edit-meta" />
            <label className="admin-label">
              Email
              <input className="admin-input" name="email" type="email" autoComplete="off" />
            </label>
            <label className="admin-label">
              Name
              <input className="admin-input" name="name" type="text" autoComplete="off" />
            </label>
            <label className="admin-label">
              Company
              <input className="admin-input" name="company" type="text" autoComplete="off" />
            </label>
            <label className="admin-label">
              Audience
              <select className="admin-input" name="audience">
                <option value="CUSTOMER">Customer — Story + Sandbox</option>
                <option value="VC">VC — Pitch deck only</option>
              </select>
            </label>
            <label className="admin-label admin-form-grid-full">
              Internal note
              <input className="admin-input" name="note" type="text" autoComplete="off" />
            </label>
            <div className="admin-form-grid-full admin-modal-actions">
              <button
                className="admin-btn-secondary"
                type="button"
                id="admin-edit-cancel"
              >
                Cancel
              </button>
              <button className="admin-btn-primary" type="submit">
                Save changes
              </button>
            </div>
            <p className="admin-error admin-form-grid-full" id="admin-edit-error" />
          </form>
        </div>
      </div>
    </div>
  );
}
